import React from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { GitMerge, X } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { MERGE_MAX, MERGE_MIN } from '../../hooks/useMergeSelection'

/**
 * Floating action bar for the entity-merge (de-duplication) flow, shared by the
 * People / Places / generic collection list surfaces. Shows the current
 * selection count and a "Merge N" button that stays disabled until at least
 * MERGE_MIN candidates are selected (and never enables past MERGE_MAX — the
 * selection hook caps additions, so this is belt-and-suspenders). "Merge" is
 * wired to `onMergeClick`, which Stage 6 fills with the review/commit modal; on
 * its own it does nothing destructive.
 *
 * Animation reuses the CLAUDE.md vocab verbatim: the bar pops in via the
 * surface spring preset (stiffness 500 / damping 30) with opacity + scale
 * 0.95→1 + y; buttons get the micro-interaction spring (400/20) with
 * whileHover/whileTap scale, and the cancel/clear buttons add the rotate:90
 * hover reserved for close/clear affordances.
 */

/**
 * Optional overrides for the empty / below-min / at-cap hint strings. Each
 * field falls back to the merge-flow default when omitted, so the merge render
 * path is unchanged when no `hintText` prop is passed.
 */
interface HintTextOverrides {
    /** Shown at count 0. */
    empty?: string
    /** Shown at 0 < count < minCount. */
    belowMin?: string
    /** Shown at count >= maxCount. */
    atCap?: string
}

interface MergeSelectionBarProps {
    /** Render + animate the bar in when true. */
    visible: boolean
    /** Number of currently-selected candidates. */
    count: number
    /** Fired when the action button is clicked (enabled only at minCount..maxCount). */
    onMergeClick: () => void
    /** Drop the selection but stay in select mode. */
    onClear: () => void
    /** Leave select mode entirely (also drops the selection). */
    onCancel: () => void
    /** Which flavor of bar to render. Defaults to the merge look. */
    variant?: 'merge' | 'prune'
    /** Label on the action button (default 'Merge'). */
    actionLabel?: string
    /** Icon on the action button (default GitMerge). */
    actionIcon?: LucideIcon
    /** Minimum selection to enable the action (default MERGE_MIN). */
    minCount?: number
    /** Maximum selection (default MERGE_MAX; pass Infinity for uncapped). */
    maxCount?: number
    /** Optional per-state hint string overrides. */
    hintText?: HintTextOverrides
}

const buttonSpring = { type: 'spring', stiffness: 400, damping: 20 } as const

const MergeSelectionBar: React.FC<MergeSelectionBarProps> = ({
    visible,
    count,
    onMergeClick,
    onClear,
    onCancel,
    variant = 'merge',
    actionLabel = 'Merge',
    actionIcon,
    minCount = MERGE_MIN,
    maxCount = MERGE_MAX,
    hintText,
}) => {
    const ActionIcon = actionIcon ?? GitMerge
    const canMerge = count >= minCount && count <= maxCount
    const emptyHint = hintText?.empty ?? 'Select duplicates to merge'
    const belowMinHint = hintText?.belowMin ?? `Select at least ${minCount}`
    const atCapHint = hintText?.atCap ?? `Max ${maxCount} reached`
    const hint =
        count === 0
            ? emptyHint
            : count < minCount
                ? belowMinHint
                : count >= maxCount
                    ? atCapHint
                    : null
    const actionButtonClass =
        variant === 'prune'
            ? 'flex items-center gap-1.5 rounded-full bg-danger px-4 py-1.5 text-sm font-medium text-white hover:bg-danger disabled:opacity-40 disabled:cursor-not-allowed'
            : 'flex items-center gap-1.5 rounded-full bg-action px-4 py-1.5 text-sm font-medium text-plum hover:bg-action disabled:opacity-40 disabled:cursor-not-allowed'

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 8 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 8 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    // Structural positioning is inline (not Tailwind) so a missing
                    // utility class can never knock the bar off-screen, and centering
                    // is done with flexbox on this full-width container so motion's
                    // inline transform (from the scale/y pop) can't clobber it — a
                    // transform-based `-translate-x-1/2` would be overridden by motion.
                    // pointer-events-none lets clicks pass through the empty strip;
                    // the pill re-enables them.
                    style={{ position: 'fixed', left: 0, right: 0, bottom: '1.5rem', zIndex: 1100, transformOrigin: 'bottom center' }}
                    className="flex justify-center px-4 pointer-events-none"
                >
                    {/* Below sm the hint takes its own line above the controls:
                        inline, it squeezed into a three-line column at 375px.
                        The pill rounds less while it is two lines tall. */}
                    <div
                        style={{ pointerEvents: 'auto' }}
                        className="flex flex-col gap-1 rounded-2xl sm:rounded-full bg-gray-900 text-white shadow-2xl pl-4 sm:pl-5 pr-2 py-2"
                    >
                    {hint && (
                        <span className="sm:hidden text-xs text-gray-400 pr-3 pt-0.5">{hint}</span>
                    )}
                    <div className="flex items-center gap-2 sm:gap-3 whitespace-nowrap">
                    <div className="flex items-center gap-2 text-sm">
                        <span className="font-semibold tabular-nums">{count}</span>
                        <span className="text-gray-300">selected</span>
                        {hint && (
                            <span className="hidden sm:inline text-xs text-gray-400 border-l border-gray-700 pl-2 ml-1">
                                {hint}
                            </span>
                        )}
                    </div>

                    {count > 0 && (
                        <motion.button
                            type="button"
                            onClick={onClear}
                            whileHover={{ scale: 1.1, rotate: 90 }}
                            whileTap={{ scale: 0.9 }}
                            transition={buttonSpring}
                            className="text-xs text-gray-300 hover:text-white underline underline-offset-2 px-1"
                            aria-label="Clear selection"
                        >
                            Clear
                        </motion.button>
                    )}

                    <motion.button
                        type="button"
                        onClick={onMergeClick}
                        disabled={!canMerge}
                        whileHover={canMerge ? { scale: 1.1 } : undefined}
                        whileTap={canMerge ? { scale: 0.9 } : undefined}
                        transition={buttonSpring}
                        className={actionButtonClass}
                    >
                        <ActionIcon className="w-4 h-4" />
                        {actionLabel}{count >= minCount ? ` ${count}` : ''}
                    </motion.button>

                    <motion.button
                        type="button"
                        onClick={onCancel}
                        whileHover={{ scale: 1.1, rotate: 90 }}
                        whileTap={{ scale: 0.9 }}
                        transition={buttonSpring}
                        className="p-1.5 text-gray-400 hover:text-white"
                        aria-label={variant === 'prune' ? 'Exit prune mode' : 'Exit merge mode'}
                    >
                        <X className="w-4 h-4" />
                    </motion.button>
                    </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

export default MergeSelectionBar
